/**
 * Stats → Overview → Historical icons and screenshots (A7Y-5020).
 *
 * Every figure comes from the nightly snapshot of the archived listing captures; `computedAt` is in the
 * heading line. Distinct assets only — by `r2Key` once stored, else by `sourceUrl`.
 */
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@a7y/tailwind-catalyst';
import type { ListingHistoryStore, StatsOverviewAssetKind, StatsOverviewAssets } from '@a7y/types';
import { formatBytesDecimal, formatInt, formatUtcInstant } from '../format';
import { Footnote } from './parts';

const STORE_LABEL: Record<ListingHistoryStore, string> = { appstore: 'App Store', play: 'Google Play' };
const KIND_LABEL: Record<StatsOverviewAssetKind, string> = { icon: 'icons', screenshot: 'screenshots' };

export function AssetsBlock({ body }: { body: StatsOverviewAssets }) {
  return (
    <div>
      <p data-testid="assets-computed-at" className="mb-2 text-xs text-zinc-500 dark:text-zinc-400">
        Counted by the nightly snapshot of {formatUtcInstant(body.computedAt)}; Refresh reloads it and does not
        recompute it.
      </p>
      <Table dense striped data-testid="assets-table">
        <TableHead>
          <TableRow>
            <TableHeader>Assets</TableHeader>
            <TableHeader>Recovered</TableHeader>
            <TableHeader>Deferred</TableHeader>
            <TableHeader>Unrecoverable</TableHeader>
            <TableHeader>Apps with recovered</TableHeader>
            <TableHeader>Stored bytes</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {body.rows.map((r) => (
            <TableRow key={`${r.store}-${r.kind}`} data-testid={`asset-row-${r.store}-${r.kind}`}>
              <TableCell className="font-medium">
                {STORE_LABEL[r.store]} {KIND_LABEL[r.kind]}
              </TableCell>
              <TableCell className="tabular-nums">{formatInt(r.recovered)}</TableCell>
              <TableCell className="tabular-nums">{formatInt(r.deferred)}</TableCell>
              <TableCell className="tabular-nums">{formatInt(r.unrecoverable)}</TableCell>
              <TableCell className="tabular-nums">{formatInt(r.appsWithRecovered)}</TableCell>
              <TableCell className="tabular-nums" title={`${formatInt(r.recoveredBytes)} B`}>
                {formatBytesDecimal(r.recoveredBytes)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Footnote testId="assets-note">
        Distinct assets, by stored key once recovered, else by source URL; an asset seen deferred in one capture
        and recovered in another is counted under both. Only icons and screenshots are counted; videos are not
        shown. Stored bytes sum the recovered assets (decimal units, 1000 bytes to the kilobyte).
      </Footnote>
    </div>
  );
}
